import useDebounce from '@/shared/hooks/useDebounce'
import { useEffect, useMemo, useRef, useState } from 'react'
import calendarApi from '../calendar.api'
import { IMaintenanceEvent, IMaintenanceEventFilters, TUnplannedMaintenanceEvent } from '../calendar.types'
import calendarUtils from '../calendar.utils'
import useHttpLoader from '@/shared/hooks/useHttpLoader'
import { IUser } from '@/pages/Users/users.types'
import timeUtils from '@/utils/time.utils'

/**
 * Хук для загрузки, фильтрации и кеширования плановых и внеплановых событий ТО за выбранный период.
 *
 * Особенности:
 * - Дебаунсит фильтры, чтобы не делать лишних запросов к API.
 * - Кэширует результаты, если новые даты входят в ранее загруженный интервал.
 * - Загружает два типа событий: плановые (events) и внеплановые (unplannedEvents).
 * - Позволяет фильтровать события по датам и по списку пользователей (через users).
 * - Возвращает только события, попадающие в указанный диапазон и соответствующие выбранным пользователям.
 *
 * @param {Object} filters - Параметры фильтрации:
 *   @property {Date} date_gte - начальная дата
 *   @property {Date} date_lte - конечная дата
 * @param {IUser[]=} users - массив пользователей для фильтрации (опционально)
 *
 * @returns {Object} Результат:
 *   @property {IMaintenanceEvent[]} events — отфильтрованные плановые события
 *   @property {TUnplannedMaintenanceEvent[]} unplannedEvents — отфильтрованные внеплановые события
 *   @property {boolean} isLoaded — признак завершения загрузки
 *   @property {function} resetLoadState — сброс состояния загрузки
 *
 * @example
 *   const { events, unplannedEvents, isLoaded } = useMaintenancePlans({ date_gte, date_lte }, users)
 */
const useMaintenancePlans = (filters: { date_gte: Date; date_lte: Date }, users?: IUser[]) => {
  const [events, setEvents] = useState<IMaintenanceEvent[]>([])
  const [unplannedEvents, setUnplannedEvents] = useState<TUnplannedMaintenanceEvent[]>([])
  const eventsTimeIntervalRef = useRef<IMaintenanceEventFilters>(null)
  const debouncedFilters = useDebounce(filters, 500)
  const [isLoaded, setIsLoaded] = useState(false)
  const { wait } = useHttpLoader()

  useEffect(() => {
    if (!filters.date_gte || !filters.date_lte) return

    if (
      events.length > 0 &&
      eventsTimeIntervalRef.current &&
      calendarUtils.isIntervalInside(filters, eventsTimeIntervalRef.current)
    ) {
      setIsLoaded(true)
      return
    }

    const job = async () => {
      const params = {
        date_lte: filters.date_lte.toISOString(),
        date_gte: filters.date_gte.toISOString(),
      }
      const resp = await calendarApi.getMaintenancePlansAllEquipment(params)
      if (resp.status === 'success') {
        const unplannedResp = await calendarApi.getUnplannedMaintenanceEventsList(params)
        if (unplannedResp.status === 'success') {
          return { ...resp, body: { events: resp.body, unplannedEvents: unplannedResp.body } }
        }
      }

      return { ...resp, body: {} }
    }

    wait(job(), (resp) => {
      if (resp.status === 'success') {
        eventsTimeIntervalRef.current = {
          date_gte: filters.date_gte,
          date_lte: filters.date_lte,
        }
        if (resp.body.events) setEvents(resp.body.events)
        if (resp.body.unplannedEvents) setUnplannedEvents(resp.body.unplannedEvents)
        setIsLoaded(true)
      }
    })
  }, [debouncedFilters])

  const filteredByUsersEvents = useMemo(() => {
    let filteredEvents = [...events]
    let filteredUnplannedEvents = [...unplannedEvents]
    if (filters.date_gte && filters.date_lte) {
      filteredEvents = events.filter(
        (e) => timeUtils.isAfter(e.ts, filters.date_gte) && timeUtils.isBefore(e.ts, filters.date_lte)
      )
      filteredUnplannedEvents = unplannedEvents.filter(
        (e) => timeUtils.isAfter(e.createdAt, filters.date_gte) && timeUtils.isBefore(e.createdAt, filters.date_lte)
      )
    }
    if (users != null && Boolean(users.length)) {
      const userIdSet = new Set(users.map((u) => u.id))
      filteredEvents = filteredEvents.filter((e) =>
        e.metadata.responsibleUsers.some((respUser) => userIdSet.has(respUser))
      )
      filteredUnplannedEvents = filteredUnplannedEvents.filter((e) => userIdSet.has(e.createdBy.id))
    }
    return {
      planned: filteredEvents,
      unplanned: filteredUnplannedEvents,
    }
  }, [events, unplannedEvents, users])

  const hasCompletedOrUnplannedEvents = useMemo(() => {
    return (
      filteredByUsersEvents.unplanned.length > 0 ||
      filteredByUsersEvents.planned.filter((e) => e.type === 'COMPLETED_MAINTENANCE').length > 0
    )
  }, [filteredByUsersEvents])

  return {
    hasCompletedOrUnplannedEvents,
    events: filteredByUsersEvents,
    isLoaded,
    resetLoadState: () => setIsLoaded(false),
  }
}

export default useMaintenancePlans
